import { useEffect, useMemo, useState } from "react";
import { useApp } from "../context/AppContext";
import { getContent } from "../lib/api";
import { Coins, RotateCcw, Info } from "lucide-react";

export default function Zakat() {
  const { t } = useApp();
  const [config, setConfig] = useState(null);
  const [vals, setVals] = useState(() => {
    try { return JSON.parse(localStorage.getItem("nour_zakat") || "{}"); } catch { return {}; }
  });
  const [debts, setDebts] = useState(() => Number(localStorage.getItem("nour_zakat_debts") || 0));
  const [goldPrice, setGoldPrice] = useState(0);
  const [silverPrice, setSilverPrice] = useState(0);

  useEffect(() => {
    getContent("zakat").then(d => {
      setConfig(d);
      if (!goldPrice)   setGoldPrice(Number(localStorage.getItem("nour_gold_price") || d.nisab_reference.default_gold_eur_per_gram));
      if (!silverPrice) setSilverPrice(Number(localStorage.getItem("nour_silver_price") || d.nisab_reference.default_silver_eur_per_gram));
    });
  }, []);

  useEffect(() => { localStorage.setItem("nour_zakat", JSON.stringify(vals)); }, [vals]);
  useEffect(() => { localStorage.setItem("nour_zakat_debts", String(debts)); }, [debts]);
  useEffect(() => { if (goldPrice)   localStorage.setItem("nour_gold_price", String(goldPrice)); }, [goldPrice]);
  useEffect(() => { if (silverPrice) localStorage.setItem("nour_silver_price", String(silverPrice)); }, [silverPrice]);

  const total = useMemo(
    () => (config?.categories || []).reduce((s, c) => s + (Number(vals[c.key]) || 0), 0),
    [vals, config]
  );
  const netWealth = Math.max(0, total - (Number(debts) || 0));
  const nisabGold   = config ? config.nisab_reference.gold_grams   * goldPrice   : 0;
  const nisabSilver = config ? config.nisab_reference.silver_grams * silverPrice : 0;
  const nisab = Math.min(nisabGold || Infinity, nisabSilver || Infinity);
  const rate = config?.nisab_reference.rate || 0.025;
  const eligible = netWealth >= nisab;
  const due = eligible ? netWealth * rate : 0;

  const reset = () => { setVals({}); setDebts(0); };

  return (
    <div className="space-y-6 fade-in" data-testid="zakat-page">
      <header className="flex items-center gap-3">
        <div className="w-12 h-12 rounded-2xl bg-nour-primary text-white flex items-center justify-center"><Coins className="w-6 h-6" /></div>
        <div>
          <h1 className="font-heading text-2xl font-bold">{t.zakat}</h1>
          <p className="text-sm text-nour-text2">{t.zakat_sub}</p>
        </div>
      </header>

      {config && (
        <>
          <section className="nour-card p-5" data-testid="zakat-nisab">
            <div className="flex items-center justify-between mb-2">
              <div className="font-heading font-semibold">Nisab ({config.nisab_reference.default_currency})</div>
              <Info className="w-4 h-4 text-nour-text2" />
            </div>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <label className="block">
                <span className="text-xs text-nour-text2">Or — {config.nisab_reference.gold_grams} g / prix par g</span>
                <input
                  type="number" step="0.01" value={goldPrice}
                  onChange={(e) => setGoldPrice(Number(e.target.value) || 0)}
                  data-testid="zakat-gold-price"
                  className="mt-1 w-full bg-emerald-50 border border-[rgba(29,158,117,0.25)] rounded-lg px-3 py-2 outline-none"
                />
                <span className="text-xs text-nour-text2">= {nisabGold.toFixed(2)} €</span>
              </label>
              <label className="block">
                <span className="text-xs text-nour-text2">Argent — {config.nisab_reference.silver_grams} g / prix par g</span>
                <input
                  type="number" step="0.01" value={silverPrice}
                  onChange={(e) => setSilverPrice(Number(e.target.value) || 0)}
                  data-testid="zakat-silver-price"
                  className="mt-1 w-full bg-emerald-50 border border-[rgba(29,158,117,0.25)] rounded-lg px-3 py-2 outline-none"
                />
                <span className="text-xs text-nour-text2">= {nisabSilver.toFixed(2)} €</span>
              </label>
            </div>
            <div className="text-xs text-nour-text2 mt-3">{config.nisab_reference.note_fr} · MAJ {config.nisab_reference.last_update_fr}</div>
          </section>

          <section className="nour-card p-5" data-testid="zakat-assets">
            <div className="font-heading font-semibold mb-3">Avoirs zakatables</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {config.categories.map(c => (
                <label key={c.key} className="block">
                  <span className="text-sm">{c.label_fr}</span>
                  <input
                    type="number" min="0" step="0.01"
                    value={vals[c.key] || ""}
                    onChange={(e) => setVals(v => ({ ...v, [c.key]: e.target.value }))}
                    data-testid={`zakat-${c.key}`}
                    placeholder="0"
                    className="mt-1 w-full bg-emerald-50 border border-[rgba(29,158,117,0.25)] rounded-lg px-3 py-2 outline-none"
                  />
                </label>
              ))}
              <label className="block sm:col-span-2">
                <span className="text-sm">Dettes immédiatement exigibles (à déduire)</span>
                <input
                  type="number" min="0" step="0.01"
                  value={debts || ""}
                  onChange={(e) => setDebts(Number(e.target.value) || 0)}
                  data-testid="zakat-debts"
                  placeholder="0"
                  className="mt-1 w-full bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 outline-none"
                />
              </label>
            </div>
            <button className="btn-ghost mt-4" onClick={reset} data-testid="zakat-reset"><RotateCcw className="w-4 h-4"/> {t.reset}</button>
          </section>

          <section className="nour-card p-6 bg-gradient-to-br from-emerald-50 to-emerald-100" data-testid="zakat-result">
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <div className="text-xs uppercase tracking-wider text-nour-text2">Total avoirs nets</div>
                <div className="font-heading text-2xl font-bold tabular-nums">{netWealth.toFixed(2)} €</div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-wider text-nour-text2">Seuil Nisab</div>
                <div className="font-heading text-2xl font-bold tabular-nums">{Number.isFinite(nisab) ? nisab.toFixed(2) : "—"} €</div>
              </div>
            </div>
            <div className={`p-4 rounded-xl ${eligible ? "bg-white" : "bg-amber-50"}`}>
              <div className="text-xs uppercase tracking-wider text-nour-text2 mb-1">Zakat due ({(rate*100).toFixed(1)}%)</div>
              <div className="font-heading text-4xl font-extrabold text-nour-primary tabular-nums">{due.toFixed(2)} €</div>
              <div className="text-sm text-nour-text2 mt-1">{eligible ? "Vous êtes redevable de la Zakat." : "Votre patrimoine net est en deçà du Nisab — non redevable."}</div>
            </div>
          </section>

          <section className="nour-card p-5" data-testid="zakat-principles">
            <div className="font-heading font-semibold mb-2">Principes essentiels</div>
            <ul className="space-y-1.5 list-disc list-inside text-sm text-nour-text">
              {config.principles_fr.map((p, i) => <li key={i}>{p}</li>)}
            </ul>
          </section>
        </>
      )}
    </div>
  );
}
